import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDashboardUi } from '../../config/dashboardUiTheme';
import { cn } from '../../lib/utils';
import { 
  useRiskAssessments, 
  useComplaints,
  useDeviations,
  useApprovalRequests
} from '../../hooks/useEntities';
import { 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  ClipboardIcon, 
  ExclamationTriangleIcon, 
  ExclamationCircleIcon, 
  ChatIcon, 
  RefreshIcon, 
  InfoIcon, 
  BookOpenIcon,
  TrendingUpSmallIcon,
  TrendingDownSmallIcon
} from '../../components/ui/Icons';

const MetricsOverview = ({ documents = [], users = [], departments = [], roles = [], onHelpClick }) => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);
  
  // Fetch data from selected modules only: Documents, Complaints, Risk Assessment, Deviations, Approvals
  const { data: riskAssessments = [] } = useRiskAssessments();
  const { data: complaints = [] } = useComplaints();
  const { data: deviations = [] } = useDeviations();
  const { data: approvalRequests = [] } = useApprovalRequests();

  // Calculate metrics
  const activeDocuments = documents.filter(doc => doc.status?.name === 'Active' || !doc.deprecated).length;
  const pendingReviews = documents.filter(doc => doc.status?.name === 'Review' || doc.status?.name === 'Pending').length;
  const expiringSoon = documents.filter(doc => {
    if (!doc.updatedAt) return false;
    const lastUpdate = new Date(doc.updatedAt);
    const monthsOld = (new Date() - lastUpdate) / (1000 * 60 * 60 * 24 * 30);
    return monthsOld > 10; // Documents older than 10 months might need review
  }).length;
  
  const complianceRate = documents.length > 0 ? 
    Math.round((activeDocuments / documents.length) * 100) : 0;

  // Risk Assessment Metrics
  const highRisks = riskAssessments.filter(risk => 
    risk.riskLevel?.name === 'High' || risk.riskLevel?.name === 'Critical'
  ).length;
  const activeRisks = riskAssessments.filter(risk => 
    risk.status?.name === 'Active' || risk.status?.name === 'Open'
  ).length;
  const mitigatedRisks = riskAssessments.filter(risk => 
    risk.status?.name === 'Mitigated' || risk.status?.name === 'Controlled'
  ).length;

  // Complaint Metrics
  const openComplaints = complaints.filter(complaint => 
    complaint.status?.name === 'Open' || complaint.status?.name === 'In Progress'
  ).length;
  const resolvedComplaints = complaints.filter(complaint => 
    complaint.status?.name === 'Resolved' || complaint.status?.name === 'Closed'
  ).length;

  // Deviation Metrics
  const openDeviations = deviations.filter(deviation => 
    deviation.status?.name === 'Open' || deviation.status?.name === 'In Progress'
  ).length;
  const closedDeviations = deviations.filter(deviation => 
    deviation.status?.name === 'Closed' || deviation.status?.name === 'Resolved'
  ).length;
  const highPriorityDeviations = deviations.filter(deviation => 
    deviation.criticality?.name === 'High' || deviation.criticality?.name === 'Critical'
  ).length;

  // Approval Request Metrics
  const pendingApprovals = approvalRequests.filter(approval => 
    approval.status?.name === 'Pending' || approval.status?.name === 'Under Review'
  ).length;
  const approvedRequests = approvalRequests.filter(approval => 
    approval.status?.name === 'Approved' || approval.status?.name === 'Completed'
  ).length;
  const rejectedApprovals = approvalRequests.filter(approval => 
    approval.status?.name === 'Rejected' || approval.status?.name === 'Declined'
  ).length;

  // Group metrics by module for better organization
  const moduleGroups = [
    {
      name: 'Document Management',
      icon: <DocumentTextIcon className="w-5 h-5" />,
      semantic: 'primary',
      metrics: [
    {
      title: 'Total Documents',
      value: documents.length.toLocaleString(),
      change: '+12%',
      changeType: 'positive',
      icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      semantic: 'primary'
    },
    {
      title: 'Active Documents',
      value: activeDocuments.toLocaleString(),
      change: '+8%',
      changeType: 'positive',
      icon: (
            <CheckCircleIcon className="w-5 h-5" />
      ),
      semantic: 'success'
    },
    {
      title: 'Pending Reviews',
      value: pendingReviews.toLocaleString(),
      change: '-5%',
      changeType: 'negative',
      icon: (
            <ClockIcon className="w-5 h-5" />
      ),
      semantic: 'warning'
        }
      ]
    },
    {
      name: 'Risk Assessment',
      icon: (
        <ExclamationTriangleIcon className="w-5 h-5" />
      ),
      semantic: 'danger',
      metrics: [
        {
          title: 'High Risks',
          value: highRisks.toLocaleString(),
          change: highRisks > 0 ? '+1' : '0',
          changeType: highRisks > 0 ? 'negative' : 'neutral',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          ),
          semantic: 'danger'
        },
        {
          title: 'Active Risks',
          value: activeRisks.toLocaleString(),
      change: '+3%',
          changeType: 'negative',
          icon: (
            <InfoIcon className="w-5 h-5" />
          ),
          semantic: 'warning'
        },
        {
          title: 'Mitigated Risks',
          value: mitigatedRisks.toLocaleString(),
          change: '+8%',
      changeType: 'positive',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          semantic: 'success'
        }
      ]
    },
    {
      name: 'Complaints',
      icon: (
        <ChatIcon className="w-5 h-5" />
      ),
      semantic: 'warning',
      metrics: [
        {
          title: 'Open Complaints',
          value: openComplaints.toLocaleString(),
          change: openComplaints > 0 ? '+2' : '0',
          changeType: openComplaints > 0 ? 'negative' : 'neutral',
          icon: (
            <ChatIcon className="w-5 h-5" />
          ),
          semantic: 'warning'
        },
        {
          title: 'Resolved Complaints',
          value: resolvedComplaints.toLocaleString(),
          change: '+22%',
          changeType: 'positive',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          semantic: 'success'
        }
      ]
    },
    {
      name: 'Deviations',
      icon: (
        <ExclamationCircleIcon className="w-5 h-5" />
      ),
      semantic: 'danger',
      metrics: [
        {
          title: 'Open Deviations',
          value: openDeviations.toLocaleString(),
          change: openDeviations > 0 ? '+1' : '0',
          changeType: openDeviations > 0 ? 'negative' : 'neutral',
          icon: (
            <ExclamationCircleIcon className="w-5 h-5" />
          ),
          semantic: 'warning'
        },
        {
          title: 'Closed Deviations',
          value: closedDeviations.toLocaleString(),
          change: '+15%',
          changeType: 'positive',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          semantic: 'success'
        },
        {
          title: 'High Priority',
          value: highPriorityDeviations.toLocaleString(),
          change: highPriorityDeviations > 0 ? '+1' : '0',
          changeType: highPriorityDeviations > 0 ? 'negative' : 'neutral',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          ),
          semantic: 'danger'
        }
      ]
    },
    {
      name: 'Approvals',
      icon: (
        <CheckCircleIcon className="w-5 h-5" />
      ),
      semantic: 'info',
      metrics: [
        {
          title: 'Pending Approvals',
          value: pendingApprovals.toLocaleString(),
          change: pendingApprovals > 0 ? '+2' : '0',
          changeType: pendingApprovals > 0 ? 'negative' : 'neutral',
          icon: (
            <ClockIcon className="w-5 h-5" />
          ),
          semantic: 'warning'
        },
        {
          title: 'Approved',
          value: approvedRequests.toLocaleString(),
          change: '+18%',
          changeType: 'positive',
          icon: (
            <CheckCircleIcon className="w-5 h-5" />
          ),
          semantic: 'success'
        },
        {
          title: 'Rejected',
          value: rejectedApprovals.toLocaleString(),
          change: rejectedApprovals > 0 ? '+1' : '0',
          changeType: 'neutral',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
          semantic: 'danger'
        }
      ]
    }
  ];

  return (
    <div className={dash.widgetShell()}>
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-xl font-semibold ${theme.text.primary}`}>
          Key Metrics
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.location.reload()}
            className={dash.btnPrimary()}
          >
            <RefreshIcon className="w-4 h-4" />
            Refresh
          </button>
        <div className={dash.pill('success')}>
          Live Data
          </div>
          <div className={`text-xs ${theme.text.muted}`}>
            Last updated: {new Date().toLocaleTimeString()}
          </div>
          {onHelpClick && (
            <button
              type="button"
              onClick={onHelpClick}
              className={`p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800 ${theme.text.secondary}`}
              aria-label="Dashboard help"
              title="Dashboard help"
            >
              <InfoIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {moduleGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="space-y-3">
            {/* Module Group Header */}
            <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
              <div className={cn(dash.iconText(group.semantic), 'shrink-0')}>
                {group.icon}
              </div>
              <h3 className={`text-base font-semibold ${theme.text.primary}`}>
                {group.name}
              </h3>
              <div className="flex-1"></div>
              <div className={`text-xs ${theme.text.muted}`}>
                {group.metrics.length} metrics
              </div>
            </div>

            {/* Module Metrics Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3">
              {group.metrics.map((metric, metricIndex) => (
                <div
                  key={metricIndex}
                  className={cn(dash.metricCard(), 'group')}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div
                      className={cn(
                        dash.iconText(metric.semantic),
                        'shrink-0 group-hover:opacity-90 transition-opacity duration-200',
                      )}
                    >
                      {metric.icon}
                    </div>
                    <div className={`flex items-center text-xs ${
                metric.changeType === 'positive'
                  ? dash.text('success')
                  : metric.changeType === 'negative'
                  ? dash.text('danger')
                  : theme.text.muted
              }`}>
                {metric.changeType === 'positive' && (
                        <TrendingUpSmallIcon className="w-3 h-3 mr-0.5" />
                )}
                {metric.changeType === 'negative' && (
                        <TrendingDownSmallIcon className="w-3 h-3 mr-0.5" />
                )}
                {metric.change}
              </div>
            </div>
            
                  <div className="space-y-0.5">
                    <div className={`text-lg font-bold ${theme.text.primary} group-hover:scale-105 transition-transform duration-200`}>
                {metric.value}
              </div>
                    <div className={`text-xs ${theme.text.secondary} truncate leading-tight`}>
                {metric.title}
              </div>
                  </div>

                  {/* Hover Effect Overlay */}
                  <div className="absolute inset-0 rounded-lg transition-all duration-200 pointer-events-none opacity-0 group-hover:opacity-100 bg-black/5 dark:bg-white/5" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetricsOverview;
